/**
 * API configuration and utility functions
 */

import { getEnv } from './env'

// API base URL
// - Local dev: '/api' (proxied by Vite)
// - Docker/Railway: injected at runtime via env-config.js
export const API_BASE_URL = getEnv('VITE_API_BASE_URL') || '/api'

/**
 * Standard fetch wrapper with error handling
 */
export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`
  
  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
    throw new Error(error.detail || `HTTP ${response.status}`)
  }

  return response.json()
}

/**
 * Order API types
 *
 * Note: Orders are now created via authenticated `authFetch` from utils/auth.tsx.
 * The backend gets the customer email from the JWT.
 * These types are kept here for reference / getOrderStatus.
 */
export interface OrderRequest {
  plan_id: 'free' | 'pro'
  billing_cycle: 'monthly' | 'annual'
  price_cents: number
}

export interface OrderResponse {
  order_id: string
  status: 'pending' | 'awaiting_payment' | 'completed' | 'cancelled' | 'refunded'
  email: string
  plan_id: string
  billing_cycle: string
  price_cents: number
  created_at: string
  checkout_url: string | null
}

/**
 * Create a new order (legacy — prefer authFetch from utils/auth.tsx)
 */
export async function createOrder(order: OrderRequest): Promise<OrderResponse> {
  return apiFetch<OrderResponse>('/orders', {
    method: 'POST',
    body: JSON.stringify(order),
  })
}

/**
 * Get order status
 */
export async function getOrderStatus(orderId: string): Promise<OrderResponse> {
  return apiFetch<OrderResponse>(`/orders/${orderId}`)
}
